import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS, buildApiUrl } from '@/constants';
import { api } from '@/utils';
import { INSIGHTS_JOB_STATUS_QUERY_KEY } from './useGenerateInsights';

// Check the job status every 5 seconds while the job is active.
const POLL_INTERVAL_MS = 5 * 1000;

// Stop polling after 20 minutes if the job is still queued or running.
const MAX_ACTIVE_POLL_MS = 20 * 60 * 1000;

// Stop polling after 3 status requests fail in a row.
const MAX_POLL_FAILURES = 3;

export type JobStatus = 'idle' | 'queued' | 'running' | 'succeeded' | 'failed';

export interface InsightsJobStatus {
    jobId?: number;
    status: JobStatus;
    error?: string | null;
    startedAt?: string | null;
    finishedAt?: string | null;
}

const useFetchInsightsJobStatus = () => {
    // Stores when the frontend started polling the current active job.
    const pollingStartedAt = useRef<number | null>(null);

    const { data, isError } = useQuery<InsightsJobStatus>({
        queryKey: INSIGHTS_JOB_STATUS_QUERY_KEY,

        queryFn: async () => {
            const url = buildApiUrl(API_ENDPOINTS.GLOBAL_PROMPT_INSIGHTS_STATUS);

            const response = await api.get<InsightsJobStatus>(url);

            return response.data;
        },

        refetchInterval: query => {
            // Stop polling when the status endpoint fails repeatedly.
            if (query.state.fetchFailureCount >= MAX_POLL_FAILURES) {
                pollingStartedAt.current = null;
                return false;
            }

            const status = query.state.data?.status;
            const isJobActive = status === 'queued' || status === 'running';

            // Finished and idle jobs do not need polling.
            if (!isJobActive) {
                pollingStartedAt.current = null;
                return false;
            }

            // Start measuring how long this browser has been polling.
            if (pollingStartedAt.current === null) {
                pollingStartedAt.current = Date.now();
            }

            const pollingDuration = Date.now() - pollingStartedAt.current;

            // Stop polling if the job stays active for too long.
            if (pollingDuration >= MAX_ACTIVE_POLL_MS) {
                pollingStartedAt.current = null;
                return false;
            }

            return POLL_INTERVAL_MS;
        },

        // Always request the latest job status when the component opens.
        refetchOnMount: 'always',
    });

    const status = data?.status;
    const isJobActive = status === 'queued' || status === 'running';

    return {
        jobId: data?.jobId,
        status,
        // Show a connection error
        error: isError ? 'Lost connection while checking analysis status' : data?.error,
        // True once status polling has given up (server unreachable).
        isConnectionError: isError,
        startedAt: data?.startedAt ?? null,
        finishedAt: data?.finishedAt ?? null,
        // Stop the loading state when the status request fails.
        isAnalyzing: isJobActive && !isError,
    };
};

export default useFetchInsightsJobStatus;
